import { openDB, type IDBPDatabase } from 'idb';

import type { ReceiptSession } from './types';

/**
 * Opt-in batch persistence to IndexedDB.
 *
 * When the user enables "Keep my batch on this device" in the settings
 * card, every terminal-state session (success / error) and every later
 * edit gets written here, keyed by session id. On the next visit the
 * store is rehydrated from this object store before React renders.
 *
 * Why opt-in: the privacy story is "your receipts never leave your
 * device", which stays accurate either way — IndexedDB sits on the
 * user's machine — but receipts are sensitive enough that sticking
 * around silently across reloads should be a deliberate choice. The
 * default is OFF, so a casual visitor's batch is gone the moment they
 * close the tab.
 *
 * Why IndexedDB and not localStorage: receipts carry the original
 * uploaded image (a few MB) and the corrected JPEG (a few hundred KB).
 * localStorage caps at ~5 MB total per origin and serialises everything
 * through JSON, which can't represent a Blob. IndexedDB stores Blobs
 * natively and has gigabyte-scale quotas (subject to user's disk).
 *
 * `originalFile` round-trip: IndexedDB will store a File but on read
 * it comes back as a generic Blob (loses .name and .lastModified).
 * `loadAllSessions` reconstructs a File from the Blob using the
 * session's `filename`, so the rest of the codebase continues to see
 * a File without caring that it's a restored one.
 */

const DB_NAME = 'receipt-ripper';
const DB_VERSION = 1;
const STORE = 'sessions';

// Lazy: open on first call, cache the promise. Closing the page invalidates
// the connection automatically; we don't need to manage explicit lifecycle.
let dbPromise: Promise<IDBPDatabase> | null = null;

function getDb(): Promise<IDBPDatabase> {
  if (dbPromise === null) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'id' });
        }
      },
    });
  }
  return dbPromise;
}

/**
 * Reconstruct a `File` from a `Blob` recovered from IndexedDB. IDB stores
 * Files but returns Blobs on read; we lose `.name` and `.lastModified`
 * but the session-level `filename` field already holds the original
 * name, so we re-wrap into a File for downstream code (worker requests,
 * the crop modal's <img>, debug bundle export) that expects a File.
 *
 * If the input is already a File, just return it (the cast happens at
 * load-time so we don't double-wrap).
 */
function ensureFile(blob: Blob, filename: string): File {
  if (blob instanceof File) return blob;
  return new File([blob], filename, { type: blob.type });
}

export async function loadAllSessions(): Promise<ReceiptSession[]> {
  try {
    const db = await getDb();
    const rows = (await db.getAll(STORE)) as ReceiptSession[];
    // IDB returns rows in primary-key (id) order — random for UUIDs.
    // Sort by the user-controlled `position` so drag-reordered batches
    // come back in the same order. Sessions without a position
    // (legacy, pre-position-field) sort to the end so they don't
    // collide with the explicit ordering.
    rows.sort(
      (a, b) =>
        (a.position ?? Number.MAX_SAFE_INTEGER) -
        (b.position ?? Number.MAX_SAFE_INTEGER),
    );
    // Repair the File wrapper on the originalFile side after IDB
    // round-trip. Other Blobs (correctedImage.blob) don't need a name,
    // they're consumed as plain Blobs.
    return rows.map((s) =>
      s.originalFile === null
        ? s
        : { ...s, originalFile: ensureFile(s.originalFile, s.filename) },
    );
  } catch (err) {
    // Storage unavailable (private mode, locked-down origin, quota exceeded):
    // graceful degradation — no persistence this session.
    console.warn('[storage] loadAllSessions failed:', err);
    return [];
  }
}

export async function saveSession(session: ReceiptSession): Promise<void> {
  try {
    const db = await getDb();
    await db.put(STORE, session);
  } catch (err) {
    console.warn('[storage] saveSession failed:', err);
  }
}

export async function deleteSession(id: string): Promise<void> {
  try {
    const db = await getDb();
    await db.delete(STORE, id);
  } catch (err) {
    console.warn('[storage] deleteSession failed:', err);
  }
}

export async function clearAllSessions(): Promise<void> {
  try {
    const db = await getDb();
    await db.clear(STORE);
  } catch (err) {
    console.warn('[storage] clearAllSessions failed:', err);
  }
}
